/**
 * AxLabs Simulation Core - Base Simulation Engine
 *
 * Abstract base class for all experiment simulations.
 * Implements the fixed-timestep accumulator pattern for deterministic physics.
 */
import { createHealthyFlags, checkSimulationHealth, getFailureMessage, SeededRandom, } from './failure.js';
// ============================================================================
// CONSTANTS
// ============================================================================
/** Default simulation timestep (120 Hz for stability) */
export const DEFAULT_DT = 1 / 120;
/** Maximum substeps per frame to prevent spiral of death */
export const MAX_SUBSTEPS = 10;
/** Default history limit for graphing */
export const DEFAULT_HISTORY_LIMIT = 10000;
// ============================================================================
// BASE SIMULATION ENGINE
// ============================================================================
/**
 * Abstract base class for all experiment simulations.
 *
 * Subclasses must implement:
 * - computeDerivatives(): Calculate rates of change
 * - computeEnergy(): Calculate energy breakdown (optional)
 */
export class BaseSimulationEngine {
    /** Current simulation time */
    time = 0;
    /** Current state variables */
    state;
    /** Current derivatives */
    derivatives = {};
    /** Current parameter values */
    parameters = {};
    /** Accumulator for fixed timestep */
    accumulator = 0;
    /** Fixed timestep */
    dt;
    /** History of states for graphing */
    history = [];
    /** Maximum history length */
    historyLimit;
    /** Initial energy for conservation checking */
    initialEnergy;
    /** Position history for stability checking */
    positionHistory = [];
    /** Seeded RNG for reproducibility */
    rng;
    /** Current simulation flags */
    flags = createHealthyFlags();
    constructor(initialState, integratorConfig = { type: 'verlet', dt: DEFAULT_DT, maxSubsteps: MAX_SUBSTEPS }, historyLimit = DEFAULT_HISTORY_LIMIT, seed = 12345) {
        this.state = { ...initialState };
        this.dt = integratorConfig.dt;
        this.historyLimit = historyLimit;
        this.rng = new SeededRandom(seed);
    }
    /**
     * Compute energy breakdown (optional, for energy conservation checking)
     */
    computeEnergy(_state, _params) {
        return undefined; // Override in subclasses that track energy
    }
    /**
     * Get bounds for state variables (for failure detection)
     */
    getBounds() {
        return undefined; // Override in subclasses
    }
    /**
     * Get a representative position value for stability checking
     */
    getPositionForStabilityCheck() {
        // Default: use first state variable
        const values = Object.values(this.state);
        return values[0] ?? 0;
    }
    // ============================================================================
    // PUBLIC API
    // ============================================================================
    /**
     * Initialize simulation with parameters
     */
    initialize(params) {
        // Validate and apply parameters
        this.parameters = this.validateParameters(params);
        // Get initial state
        this.state = this.getInitialState(this.parameters);
        // Reset time and history
        this.time = 0;
        this.accumulator = 0;
        this.history = [];
        this.positionHistory = [];
        this.flags = createHealthyFlags();
        // Compute initial derivatives
        this.derivatives = this.computeDerivatives(this.state, this.parameters, this.time);
        // Compute and store initial energy
        const energy = this.computeEnergy(this.state, this.parameters);
        if (energy) {
            this.initialEnergy = energy.total;
        }
        // Record initial state
        this.recordState();
    }
    /**
     * Advance simulation by one frame (variable dt)
     * Uses fixed timestep accumulator pattern internally
     */
    update(frameDt) {
        // Clamp frame dt to prevent spiral of death
        const clampedDt = Math.min(frameDt, this.dt * MAX_SUBSTEPS);
        this.accumulator += clampedDt;
        // Run fixed timesteps
        let steps = 0;
        while (this.accumulator >= this.dt && steps < MAX_SUBSTEPS) {
            this.step(this.dt);
            this.accumulator -= this.dt;
            steps++;
        }
        return this.getState();
    }
    /**
     * Advance simulation by exactly one fixed timestep
     */
    step(dt) {
        // Skip if simulation has failed
        if (!this.flags.isStable) {
            return this.getState();
        }
        // Compute derivatives before integration
        this.derivatives = this.computeDerivatives(this.state, this.parameters, this.time);
        // Apply integration
        this.integrate(dt);
        // Advance time
        this.time += dt;
        // Update position history for stability checking
        this.positionHistory.push(this.getPositionForStabilityCheck());
        if (this.positionHistory.length > 100) {
            this.positionHistory.shift();
        }
        // Check for failures
        const currentState = this.getState();
        this.flags = checkSimulationHealth(currentState, this.initialEnergy, this.positionHistory, this.getBounds());
        // Add failure message if needed
        if (!this.flags.isStable) {
            const message = getFailureMessage(this.flags);
            if (message) {
                this.flags = { ...this.flags, failureMessage: message };
            }
        }
        // Record state for history
        this.recordState();
        return currentState;
    }
    /**
     * Get current state without advancing
     */
    getState() {
        const energy = this.computeEnergy(this.state, this.parameters);
        const baseState = {
            time: this.time,
            dt: this.dt,
            variables: { ...this.state },
            derivatives: { ...this.derivatives },
            flags: { ...this.flags },
        };
        // Only include energy if it exists (satisfies exactOptionalPropertyTypes)
        if (energy !== undefined) {
            return { ...baseState, energy };
        }
        return baseState;
    }
    /**
     * Reset simulation to initial conditions
     */
    reset() {
        this.initialize(this.parameters);
    }
    /**
     * Get current parameter values
     */
    getParameters() {
        return { ...this.parameters };
    }
    /**
     * Update a parameter value
     */
    setParameter(id, value) {
        const paramDef = this.config.parameters.find(p => p.id === id);
        if (!paramDef) {
            throw new Error(`Unknown parameter: ${id}`);
        }
        // Clamp to bounds
        const clampedValue = Math.max(paramDef.min, Math.min(paramDef.max, value));
        this.parameters[id] = clampedValue;
        // Some parameters may require reset (e.g., initial conditions)
        // Subclasses can override to handle parameter changes differently
        this.reset();
    }
    /**
     * Serialize state for save/replay
     */
    serialize() {
        return JSON.stringify({
            time: this.time,
            state: this.state,
            parameters: this.parameters,
            initialEnergy: this.initialEnergy,
        });
    }
    /**
     * Deserialize and restore state
     */
    deserialize(data) {
        const parsed = JSON.parse(data);
        this.time = parsed.time;
        this.state = parsed.state;
        this.parameters = parsed.parameters;
        this.initialEnergy = parsed.initialEnergy;
        // Recompute derivatives
        this.derivatives = this.computeDerivatives(this.state, this.parameters, this.time);
        this.flags = createHealthyFlags();
    }
    /**
     * Get history for graphing
     */
    getHistory() {
        return this.history;
    }
    /**
     * Clear history
     */
    clearHistory() {
        this.history = [];
    }
    // ============================================================================
    // PROTECTED HELPERS
    // ============================================================================
    /**
     * Record current state to history
     */
    recordState() {
        const state = this.getState();
        this.history.push(state);
        // Limit history size
        if (this.history.length > this.historyLimit) {
            this.history.shift();
        }
    }
    /**
     * Validate parameters against config
     */
    validateParameters(params) {
        const validated = {};
        for (const paramDef of this.config.parameters) {
            let value = params[paramDef.id];
            // Use default if not provided
            if (value === undefined) {
                value = paramDef.default;
            }
            // Clamp to bounds
            value = Math.max(paramDef.min, Math.min(paramDef.max, value));
            validated[paramDef.id] = value;
        }
        return validated;
    }
}
//# sourceMappingURL=engine.js.map